import React, { useMemo, useEffect, useState } from "react";
import moment from "moment";
import TableContainer from "./TableContainer";
import PatientLoader from "../../../common/PatientLoader";
import "./PatientDataTableStyles.css";
import RiskPill from "../../../common/RiskPill";
import { getFullName, getInitials } from "../../../../util/helpers";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";

const PatientDataTable = ({ currentUrl, flag }) => {
  let navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [localLoading, setLocalLoading] = useState(false);
  const {
    loading,
    patientData,
    newPatient,
    bhiPatient,
    invitedPatient,
    inActivePatient,
  } = useSelector((state) => state.patient);
  const [status, setStatus] = useState();
  const [filterData, setFilteredData] = useState([]);
  const handleNavigation = (link = "123") => {
    navigate(`/patients/${link}`);
  };

  const handleBifurcation = () => {
    const handleFiltering = (data, bhi = false) => {
      let filteredData = JSON.parse(JSON.stringify(data));
      if (flag) {
        filteredData = filteredData.filter((item) => {
          let temp = item.flag || "";
          if (temp.toLowerCase() === flag) {
            return item;
          }
        });
      }

      if (bhi) {
        filteredData = filteredData.filter((item) => {
          const bhiEligible = JSON.parse(item?.bhi_eligible);
          const bhiEnrolled = JSON.parse(item?.bhi_enabled);
          if (bhiEligible && !bhiEnrolled) {
            return item;
          }
        });
      }

      return filteredData;
    };

    if (currentUrl === "all") {
      if (flag) {
        const temp = handleFiltering(patientData);
        setFilteredData(temp);
      } else {
        setFilteredData(patientData);
      }
    } else if (currentUrl === "new") {
      if (flag) {
        const temp = handleFiltering(newPatient);
        setFilteredData(temp);
      } else {
        setFilteredData(newPatient);
      }
    } else if (currentUrl === "bhi") {
      if (flag) {
        const temp = handleFiltering(bhiPatient);
        setFilteredData(temp);
      } else {
        if (status === "eligible") {
          const temp = handleFiltering(patientData, true);
          setFilteredData(temp);
        } else {
          setFilteredData(bhiPatient);
        }
      }
    } else if (currentUrl === "inactive") {
      if (flag) {
        const temp = handleFiltering(inActivePatient);
        setFilteredData(temp);
      } else {
        setFilteredData(inActivePatient);
      }
    } else if (currentUrl === "invited") {
      if (flag) {
        const temp = handleFiltering(invitedPatient);
        setFilteredData(temp);
      } else {
        setFilteredData(invitedPatient);
      }
    }
  };

  useEffect(() => {
    const tempCurrentUrl = window.location.pathname
      .split("/patients/")[1]
      .replace("/", "");
    const tempStatus = searchParams.get("status");
    if (tempCurrentUrl === "bhi" && tempStatus !== null) {
      setStatus(tempStatus);
    }
  }, [searchParams]);

  useEffect(() => {
    setLocalLoading(true);
    if (loading) return;
    handleBifurcation();
    setLocalLoading(false);
  }, [currentUrl, status, loading]);

  useEffect(() => {
    handleBifurcation();
  }, [patientData, newPatient, bhiPatient, invitedPatient, inActivePatient]);

  let renderAdultColumns = [
    {
      Header: "Name",
      accessor: (d) => getFullName(d),
      Cell: ({ cell }) => {
        return (
          <span style={{ textTransform: "capitalize" }}>
            <img
              alt="User's avatar"
              style={{ height: "40px", borderRadius: "100%" }}
              src={`https://placehold.co/70x70/f1f1f5/395E9D/?text=${getInitials(
                cell.value
              )}`}
            />
            &nbsp;&nbsp;{cell.value}
          </span>
        );
      },
    },
    {
      Header: "Status",
      accessor: "fire_flag",
      sortType: (rowA, rowB, columnId) => {
        const aValue = String(rowA.values[columnId]);
        const bValue = String(rowB.values[columnId]);
        return aValue.localeCompare(bValue);
      },
      Cell: ({ cell }) => {
        return cell?.value?.length > 0 ? (
          <RiskPill
            show_icon={false}
            styles={{ width: "fit-content", gap: "5px" }}
            type={"urgent"}
          />
        ) : (
          <></>
        );
      },
    },
    {
      Header: "Flag",
      accessor: "flag",
      Cell: ({ cell }) => {
        return (
          <RiskPill
            icon_type="flag"
            show_text={false}
            styles={{ width: "fit-content", gap: "5px" }}
            type={cell.value}
          />
        );
      },
    },
    {
      Header: "BHI Status",
      accessor: (d) => `${d?.bhi_enabled}||${d?.bhi_eligible}`,
      Cell: ({ cell }) => {
        let temp = cell.value.split("||");
        let bhi_enabled = false;
        let bhi_eligible = false;
        if (temp[0] !== "undefined" && temp[1] !== "undefined") {
          bhi_enabled = JSON.parse(temp[0]);
          bhi_eligible = JSON.parse(temp[1]);
        }

        let value = "not eligible";
        if (bhi_eligible) {
          if (bhi_enabled) {
            value = "enrolled";
          } else {
            value = "eligible";
          }
        }
        return (
          <RiskPill
            styles={{ width: "fit-content", gap: "5px" }}
            type={value}
            show_icon={false}
          />
        );
      },
    },
    {
      Header: "Last Active",
      accessor: "last_active",
      //disableSortBy: true,
      sortType: (a, b) => {
        if (a === null || a === undefined || a === "" || a === "Not Available")
          return 1; // place null values at the bottom
        if (b === null || b === undefined || b === "" || b === "Not Available")
          return -1;
        return (
          new Date(b?.values?.last_active) - new Date(a?.values?.last_active)
        );
      },

      Cell: ({ cell }) => {
        return (
          <span style={{ textTransform: "capitalize" }}>
            {cell.value
              ? moment(cell.value).format("MMM Do YYYY, h:mma")
              : "Not Available"}
          </span>
        );
      },
    },
    {
      Header: "Physician",
      accessor: "physician",
      Cell: ({ cell }) => {
        return (
          <span style={{ textTransform: "capitalize" }}>
            {cell.value ? cell.value : "Not Available"}
          </span>
        );
      },
    },
    {
      Header: "Cell Number",
      accessor: "cell_number",
      disableSortBy: true,
      Cell: ({ cell }) => {
        return (
          <span style={{ textTransform: "capitalize" }}>
            {cell.value ? cell.value : "Not Added"}
          </span>
        );
      },
    },
    {
      Header: "Action",
      accessor: "id",
      disableSortBy: true,
      Cell: ({ cell }) => {
        return (
          <button
            onClick={() => handleNavigation(cell.value)}
            className="primary-btn-small"
          >
            View
          </button>
        );
      },
    },
  ];

  let renderYouthColumns = [
    {
      Header: "Name",
      accessor: (d) => getFullName(d),
      Cell: ({ cell }) => {
        return (
          <span style={{ textTransform: "capitalize" }}>
            <img
              alt="User's avatar"
              style={{ height: "40px", borderRadius: "100%" }}
              src={`https://placehold.co/70x70/f1f1f5/395E9D/?text=${getInitials(
                cell.value
              )}`}
            />
            &nbsp;&nbsp;{cell.value}
          </span>
        );
      },
    },
    {
      Header: "Status",
      accessor: "fire_flag",
      sortType: (rowA, rowB, columnId) => {
        const aValue = String(rowA.values[columnId]);
        const bValue = String(rowB.values[columnId]);
        return aValue.localeCompare(bValue);
      },
      Cell: ({ cell }) => {
        return cell?.value?.length > 0 ? (
          <RiskPill
            show_icon={false}
            styles={{ width: "fit-content", gap: "5px" }}
            type={"urgent"}
          />
        ) : (
          <></>
        );
      },
    },
    {
      Header: "Flag",
      accessor: "flag",
      Cell: ({ cell }) => {
        return (
          <RiskPill
            icon_type="flag"
            show_text={false}
            styles={{ width: "fit-content", gap: "5px" }}
            type={cell.value}
          />
        );
      },
    },
    {
      Header: "SSRI Status",
      accessor: "ssri_status",
      sortType: (rowA, rowB, columnId) => {
        // Custom sorting logic for "SSRI Status" column
        const getStatusOrder = status => {
          if (status === true) return 1; // Active
          if (status === false) return 2; // Inactive
          return 3; // Not Available
        };
  
        const orderA = getStatusOrder(rowA.values[columnId]);
        const orderB = getStatusOrder(rowB.values[columnId]);
  
        return orderA - orderB;
      },
      Cell: ({ cell }) => {
        return (
          <span style={{ textTransform: "capitalize" }}>
            {cell.value ? "Active " : "Inactive"}
            {cell.value === undefined ||
              (cell.value === null && "Not Available")}
          </span>
        );
      },
    },
    {
      Header: "BHI Status",
      accessor: (d) => `${d?.bhi_enabled}||${d?.bhi_eligible}`,
      Cell: ({ cell }) => {
        let temp = cell.value.split("||");
        let bhi_enabled = false;
        let bhi_eligible = false;
        if (temp[0] !== "undefined" && temp[1] !== "undefined") {
          bhi_enabled = JSON.parse(temp[0]);
          bhi_eligible = JSON.parse(temp[1]);
        }

        let value = "not eligible";
        if (bhi_eligible) {
          if (bhi_enabled) {
            value = "enrolled";
          } else {
            value = "eligible";
          }
        }
        return (
          <RiskPill
            styles={{ width: "fit-content", gap: "5px" }}
            type={value}
            show_icon={false}
          />
        );
      },
    },
    {
      Header: "Last Active",
      accessor: "last_active",
      //disableSortBy: true,
      sortType: (a, b) => {
        if (a === null || a === undefined || a === "" || a === "Not Available")
          return 1; // place null values at the bottom
        if (b === null || b === undefined || b === "" || b === "Not Available")
          return -1;
        return (
          new Date(b?.values?.last_active) - new Date(a?.values?.last_active)
        );
      },

      Cell: ({ cell }) => {
        return (
          <span style={{ textTransform: "capitalize" }}>
            {cell.value
              ? moment(cell.value).format("MMM Do YYYY, h:mma")
              : "Not Available"}
          </span>
        );
      },
    },
    {
      Header: "Physician",
      accessor: "physician",
      Cell: ({ cell }) => {
        return (
          <span style={{ textTransform: "capitalize" }}>
            {cell.value ? cell.value : "Not Available"}
          </span>
        );
      },
    },
    {
      Header: "Cell Number",
      accessor: "cell_number",
      disableSortBy: true,
      Cell: ({ cell }) => {
        return (
          <span style={{ textTransform: "capitalize" }}>
            {cell.value ? cell.value : "Not Added"}
          </span>
        );
      },
    },
    {
      Header: "Action",
      accessor: "id",
      disableSortBy: true,
      Cell: ({ cell }) => {
        return (
          <button
            onClick={() => handleNavigation(cell.value)}
            className="primary-btn-small"
          >
            View
          </button>
        );
      },
    },
  ];

  let renderColumns = renderAdultColumns;
  if (process.env.REACT_APP_DASHBOARD_TYPE === "youth") {
    renderColumns = renderYouthColumns;
  }
  if (currentUrl === "invited") {
    renderColumns.pop();
  }

  const columns = useMemo(() => renderColumns, [localLoading]);

  return (
    <>
      {localLoading ? (
        <PatientLoader />
      ) : (
        <TableContainer
          columns={columns}
          data={filterData}
          currentUrl={currentUrl}
          flag={flag}
          loading={localLoading}
        />
      )}
    </>
  );
};

export default PatientDataTable;
